import { FC, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon, { IconNames } from 'components/Icon';
import { useAppDispatch } from 'helpers/hooks';
import { userDates } from 'store/viewPreferences/slice';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';

interface Props {
  setIsCustom?: (val: boolean) => void;
  labels: { label: string; value: string | Date | number | (() => string) }[];
  screen?: DashboardScreen;
}

export const DateFilters: FC<Props> = ({ setIsCustom, labels, screen }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  // Add responsive detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter out "Custom" label on desktop
  const filteredLabels =
    isMobile || window.location.href.includes('dashboard')
      ? labels
      : labels.filter(label => label.label !== 'Custom');

  const dispatch = useAppDispatch();

  useEffect(() => {
    const routeKey = `dateFilterActiveIndex_${location.pathname}`;
    const savedIndex = sessionStorage.getItem(routeKey);
    if (savedIndex !== null) {
      setActiveIndex(parseInt(savedIndex));
      const idx = parseInt(savedIndex);
      const isLast = idx === filteredLabels.length - 1;
      setIsCustom && setIsCustom(isLast);
      if (!isLast) {
        const option = filteredLabels[idx];
        if (option) {
          handleChangeDates(option.value);
        }
      }
    }
  }, [location.pathname, filteredLabels]);

  const handleChangeDates = (optionValue: Props['labels'][number]['value']) => {
    const startDateVal = typeof optionValue === 'function' ? optionValue() : optionValue;

    // A copy, so a Date passed in as a label value is never mutated
    const start = new Date(startDateVal);
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setHours(23, 59, 59, 999);

    dispatch(userDates({ start: start.toISOString(), end: end.toISOString() }));
  };
  const [activeIndex, setActiveIndex] = useState(filteredLabels.length - 1);

  const handleSelect = (option, index) => {
    if (index !== activeIndex && screen) {
      trackEvent('Date Filter Changed', { screen, filter_label: option.label });
    }
    setActiveIndex(index);

    const routeKey = `dateFilterActiveIndex_${location.pathname}`;
    sessionStorage.setItem(routeKey, index.toString());
    setIsCustom && setIsCustom(index === filteredLabels.length - 1);
    if (index !== filteredLabels.length - 1) handleChangeDates(option.value);
  };

  return (
    <div className="date-filter-container">
      {filteredLabels.map((option, index) => (
        <button
          key={index}
          className={`date-filter-item ${activeIndex === index ? 'active' : ''}`}
          onClick={() => handleSelect(option, index)}
        >
          <span className="oath-body inkBlue">{option.label}</span>
          {option.label === 'Custom' && (
            <Icon
              name={activeIndex === index ? IconNames.ChevronUp : IconNames.ChevronDown}
              size={18}
            />
          )}
        </button>
      ))}
    </div>
  );
};
